/**
 * Data mapping utilities for transforming API responses into FME tree state.
 * Extracted from FinancialModelEditor.tsx (Phase 3A).
 */

import FieldsInitializer from '../../../components/ModelDataFields/Input/Initializer';
import IModelData from '../../../types/ModelData';
import IModelDataResponse from '../../../types/ModelDataResponse';
import { useEditorStore } from '../../../shared/stores/useEditorStore';

export const TreeInit = {
    rootId: '42',
    items: {
        '42': {
            id: '42',
            name: 'root',
            type: 'root',
            position: 0,
            expanded: false,
            editing: false,
            anchorEl: null,
            deleteModalOpen: false,
            childrens: [],
            possibleChildrens: [],
            parentId: null,
        },
    }
};

export const findCurrentScenarioByRef = (entity: any) => {
    const scenarios = entity.modelScenarios || entity.model_scenarios;
    if (!scenarios || !scenarios.length) return;

    if (!entity.currentScenario) {
        entity.currentScenario = scenarios[0];
    } else {
        scenarios.forEach((scenario: any) => {
            if (entity.currentScenario.id == scenario.id) {
                entity.currentScenario = scenario;
            }
        });
    }
};

export const mapModelData = (modelData: IModelData, editingId?: string): any => {
    return {
        id: modelData.id.toString(),
        name: modelData.name,
        type: modelData.type,
        kind: modelData.kind,
        position: modelData.position,
        childrens: modelData.childrens.map((e: any) => e.toString()),
        possibleChildrens: modelData.possible_childrens,
        childrensDefinition: modelData.childrens_definition,
        parentId: (modelData.parent_id) ? modelData.parent_id.toString() : null,
        relatedModelId: modelData.related_model_id,
        fields: FieldsInitializer(modelData.fields),
        has_related_output: modelData.has_related_output,
        relatedModelDataId: modelData.related_model_id,
        isInconsiderable: modelData.is_inconsiderable,
        isDeletable: modelData.is_deletable,
        expanded: useEditorStore.getState().expandedNodes.has(modelData.kind + modelData.id.toString()),
        editing: modelData.id.toString() == editingId ? true : false,
        anchorEl: null,
        sensitivityModelData: null,
        deleteModalOpen: false,
        detailsModalOpen: false,
        documentation: modelData.documentation,
        compute_error: modelData.compute_error || null,
    };
};

export const mapField = (items: any): Record<string, any> => {
    const ret: Record<string, any> = {};
    Object.values(items).map((md: any) => {
        Object.values(md['fields']).map((field: any) => {
            ret[field.id] = field;
        });
    });
    return ret;
};

export const mapModelDataList = (modelDataList: Array<IModelData>, editingId?: string): any => {
    const result: Record<string, any> = {};
    for (const elem of modelDataList) {
        result[elem.id.toString()] = mapModelData(elem, editingId);
    }
    return result;
};

export const findRootItemId = (modelDataList: Array<IModelData>): number => {
    const root = modelDataList.filter((modelData) => modelData.parent_id == null)[0];
    if (!root) return 0;
    return root.id;
};

export const mapApiResponse = (response: IModelDataResponse, editingId?: string) => {
    const input_items = mapModelDataList(response['input'], editingId);
    const output_items = mapModelDataList(response['output'], editingId);

    const result = {
        isComputed: response['is_computed'],
        isComputing: response['is_computing'],
        isSync: false,
        dashboardComputed: response['dashboard_computed'],
        Input: {
            tree: {
                items: input_items,
                rootId: findRootItemId(response['input']).toString()
            },
            field_values: mapField(input_items)
        },
        Output: {
            tree: {
                items: output_items,
                rootId: findRootItemId(response['output']).toString()
            },
            field_values: mapField(output_items)
        }
    };
    return result;
};

// WARNING: copyFields and FieldsInitializer (via initTimeSerie) mutate `dest`
// in place. Callers MUST pass a deep-enough clone if the object is shared with
// React state or TanStack Query cache to avoid stale-reference bugs.
export const copyFields = (source: any, dest: any) => {
    if (!source?.fields || !dest?.fields) return;
    dest.compute_error = source.compute_error || null;
    Object.entries(source.fields).map(([key, field]: any) => {
        if (dest.fields[key]) {
            dest.fields[key].value = field.value;
            dest.fields[key].compute_error = field.compute_error;
        }
    });
    dest.fields = FieldsInitializer(dest.fields);
};

export const copyInputFields = (source: any, dest: any) => {
    if (!source.fields || !dest.fields) return;
    Object.entries(source.fields).map(([key, field]: any) => {
        if (dest.fields[key]) {
            dest.fields[key].compute_error = field.compute_error || null;
        }
    });
    dest.compute_error = source.compute_error || null;
};

export const mapOutputApiResponseById = (response: IModelDataResponse, state: any) => {
    if (!response?.['output']) return;
    for (const elem of response['output']) {
        const currentObj = state.Output.tree.items[elem.id.toString()];
        if (currentObj) {
            copyFields(elem, currentObj);
        }
    }
};

export const mapApiOutputResponse = (response: any, state: any) => {
    const output_items = mapModelDataList(response['output']);

    state['Output'] = {
        tree: {
            items: output_items,
            rootId: findRootItemId(response['output']).toString()
        },
        field_values: mapField(output_items)
    };
};

export const applyInputErrors = (errors: Array<{field_id: number, field_name: string, model_data_id: number, error: string}>, state: any) => {
    Object.values(state.Input.tree.items).map((obj: any) => {
        obj.compute_error = null;
        if (obj.fields) Object.values(obj.fields).map((field: any) => {
            field.compute_error = null;
        });
    });

    for (const err of errors) {
        const item = state.Input.tree.items[err.model_data_id.toString()];
        if (item && item.fields?.[err.field_name]) {
            item.fields[err.field_name].compute_error = err.error;
            item.compute_error = err.error;
        }
    }
};

export const setExpandedRef = (state: any) => {
    const entries: Array<[string, boolean]> = [];
    Object.values(state['Input'].tree.items).forEach((item: any) => { entries.push(['Input' + item.id, item.expanded]); });
    Object.values(state['Output'].tree.items).forEach((item: any) => { entries.push(['Output' + item.id, item.expanded]); });
    useEditorStore.getState().bulkSetExpanded(entries);
};

export const initSensitivitiesOutput = (state: any) => {
    Object.values(state.Output.tree.items).map((obj: any) => {
        if (obj.fields) Object.values(obj.fields).map((field: any) => {
            if (field.type == 'float') {
                field.value.value = null;
            } else if (field.type == 'time_serie') {
                field.value.value.data = [];
            } else if (field.type == 'grid' || field.type == 'dynamic_grid') {
                field.value.value = [];
            }
            field.compute_error = null;
        });
    });
    Object.values(state.Input.tree.items).map((obj: any) => {
        obj.compute_error = null;
        if (obj.fields) Object.values(obj.fields).map((field: any) => {
            field.compute_error = null;
        });
    });
};

export const getItemComputeError = (item: any, tree: any): string | null => {
    if (!item) return null;
    let firstError: string | null = null;
    if (item.compute_error && !firstError) {
        firstError = item.compute_error;
    }
    if (item.fields) {
        Object.values(item.fields).map((field: any) => {
            if (field.compute_error && !firstError) {
                firstError = field.compute_error;
            }
        });
    }
    if (!firstError && item.childrens) {
        item.childrens.map((id: string) => {
            const childItem = tree.items[id];
            const error = getItemComputeError(childItem, tree);
            if (error && !firstError) {
                firstError = error;
            }
        });
    }
    return firstError;
};

export const getComputeError = (rootOutputItem: any, tree: any): string | null => {
    if (!rootOutputItem?.childrens) return null;
    let firstErrorMessage: string | null = null;

    rootOutputItem.childrens.map((id: string) => {
        const item = tree.items[id];
        const error = getItemComputeError(item, tree);
        if (error && !firstErrorMessage) {
            firstErrorMessage = error;
        }
    });

    return firstErrorMessage;
};
